import BreadCrumb from "Components/Common/BreadCrumb";
import { useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Nav,
  NavItem,
  TabContent,
  TabPane,
  NavLink,
} from "reactstrap";
import classnames from "classnames";
import { useFormik } from "formik";
import * as Yup from "yup";
import PersonalDetails from "Components/Settings/Profile/PersonalDetails";
import BusinessInfo from "Components/Settings/Profile/BusinessInfo";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { businessProfileSetup, profileSetup } from "slices/thunks";
import {
  VALIDATION_LIMITS,
  VALIDATION_MESSAGES,
  VALIDATION_PATTERNS,
  createNameValidation,
  createAddressValidation,
  createLocationValidation,
} from "Components/constants/validation";

export interface PersonalDetailsValues {
  name: string;
  email: string;
  phone: string;
}

export interface BusinessInfoValues {
  businessName: string;
  phone: string;
  streetAddress: string;
  city: string;
  // state: string;
  zipCode: string;
  country: string;
  isVATRegistered: boolean;
  vatNumber: string;
}

// Helper function to format phone number from backend
const formatPhoneNumber = (phone: string | null | undefined): string => {
  if (!phone) return "";

  // Remove any existing formatting
  const cleanValue = phone.replace(/[^\d]/g, "");

  if (cleanValue.length === 0) return "";

  // Check if it already has country code
  const countryCode = cleanValue.startsWith("44")
    ? cleanValue.substring(0, 2)
    : "44";
  const phoneNumber = cleanValue.startsWith("44")
    ? cleanValue.substring(2)
    : cleanValue;

  // Return formatted as (+44) XXXXXXXXXX (with space)
  return `(+${countryCode}) ${phoneNumber}`;
};

const Profile = () => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<string>("1");
  const { user, isLoading } = useAppSelector((state) => state?.auth);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [color, setColor] = useState(user?.company?.brand_color ?? "#38b6ff");
  const [isVATRegistered, setIsVATRegistered] = useState<boolean>(
    !!user?.company?.vat_number,
  );
  const [isShowCompanyName, setIsShowCompanyName] = useState<boolean>(
    user?.company?.is_company_name_show ?? false,
  );

  const personalDetailValidation = useFormik<PersonalDetailsValues>({
    enableReinitialize: true,
    initialValues: {
      name: user?.name!,
      email: user?.email!,
      phone: formatPhoneNumber(user?.phone),
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required(VALIDATION_MESSAGES.REQUIRED("Your Name"))
        .max(
          VALIDATION_LIMITS.NAME.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH("Name", VALIDATION_LIMITS.NAME.MAX),
        )
        .trim()
        .min(
          VALIDATION_LIMITS.NAME.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH("Name", VALIDATION_LIMITS.NAME.MIN),
        )
        .test(
          "valid-name",
          createNameValidation("Name").message,
          createNameValidation("Name").test,
        ),
      phone: Yup.string()
        .required(VALIDATION_MESSAGES.REQUIRED("Your Phone Number"))
        .matches(VALIDATION_PATTERNS.PHONE, VALIDATION_MESSAGES.INVALID_PHONE),
      email: Yup.string()
        .required(VALIDATION_MESSAGES.REQUIRED("Your Email"))
        .email(VALIDATION_MESSAGES.INVALID_EMAIL),
    }),

    onSubmit: (values) => {
      console.log(values);

      dispatch(
        profileSetup({
          name: values.name?.trim(),
          phone: values.phone.replace(/[()]/g, "").replace(/\s+/g, ""),
          _method: "put",
          avatar: selectedFile,
        }),
      );
    },
  });

  const businessInfoValidation = useFormik<BusinessInfoValues>({
    enableReinitialize: true,
    initialValues: {
      businessName: user?.company?.name ?? "",
      phone: formatPhoneNumber(user?.company?.billing_details?.phone),
      streetAddress: user?.company?.address?.address!,
      city: user?.company?.address?.city!,
      // state: user?.company?.address?.state ?? "",
      zipCode: user?.company?.address?.postcode!,
      country: user?.company?.address?.country ?? "",
      isVATRegistered: user?.company?.vat_number ? true : false,
      vatNumber: user?.company?.vat_number ?? "",
    },
    validationSchema: Yup.object({
      businessName: Yup.string()
        .required(VALIDATION_MESSAGES.REQUIRED("Your Business Name"))
        .trim()
        .min(
          VALIDATION_LIMITS.BUSINESS_NAME.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH(
            "Business Name",
            VALIDATION_LIMITS.BUSINESS_NAME.MIN,
          ),
        )
        .max(
          VALIDATION_LIMITS.BUSINESS_NAME.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Business Name",
            VALIDATION_LIMITS.BUSINESS_NAME.MAX,
          ),
        )
        .test(
          "valid-business-name",
          createNameValidation("Business Name").message,
          createNameValidation("Business Name").test,
        ),
      phone: Yup.string()
        .required(VALIDATION_MESSAGES.REQUIRED("Your Phone Number"))
        .matches(VALIDATION_PATTERNS.PHONE, VALIDATION_MESSAGES.INVALID_PHONE),
      streetAddress: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("Your Street Address"))
        .min(
          VALIDATION_LIMITS.ADDRESS.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH(
            "Street Address",
            VALIDATION_LIMITS.ADDRESS.MIN,
          ),
        )
        .max(
          VALIDATION_LIMITS.ADDRESS.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Street Address",
            VALIDATION_LIMITS.ADDRESS.MAX,
          ),
        )
        .test(
          "valid-address",
          createAddressValidation("Street Address").message,
          createAddressValidation("Street Address").test,
        ),
      city: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("Your City"))
        .min(
          VALIDATION_LIMITS.LOCATION.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH(
            "City",
            VALIDATION_LIMITS.LOCATION.MIN,
          ),
        )
        .max(
          VALIDATION_LIMITS.LOCATION.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "City",
            VALIDATION_LIMITS.LOCATION.MAX,
          ),
        )
        .test(
          "valid-city",
          createLocationValidation("City").message,
          createLocationValidation("City").test,
        ),
      // state: Yup.string()
      //   .trim()
      //   .required(VALIDATION_MESSAGES.REQUIRED("Your State"))
      //   .min(
      //     VALIDATION_LIMITS.LOCATION.MIN,
      //     VALIDATION_MESSAGES.MIN_LENGTH(
      //       "State",
      //       VALIDATION_LIMITS.LOCATION.MIN
      //     )
      //   )
      //   .max(
      //     VALIDATION_LIMITS.LOCATION.MAX,
      //     VALIDATION_MESSAGES.MAX_LENGTH(
      //       "State",
      //       VALIDATION_LIMITS.LOCATION.MAX
      //     )
      //   )
      //   .test(
      //     "valid-state",
      //     createLocationValidation("State").message,
      //     createLocationValidation("State").test
      //   ),
      zipCode: Yup.string()
        .required(VALIDATION_MESSAGES.REQUIRED("your postcode"))
        .matches(
          VALIDATION_PATTERNS.ZIP_CODE,
          "Postcode must contain only letters, numbers, and spaces",
        )
        .max(
          VALIDATION_LIMITS.ZIP_CODE.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "postcode",
            VALIDATION_LIMITS.ZIP_CODE.MAX,
          ),
        ),
      country: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("Your Country"))
        .min(
          VALIDATION_LIMITS.LOCATION.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH(
            "Country",
            VALIDATION_LIMITS.LOCATION.MIN,
          ),
        )
        .max(
          VALIDATION_LIMITS.LOCATION.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Country",
            VALIDATION_LIMITS.LOCATION.MAX,
          ),
        )
        .test(
          "valid-country",
          createLocationValidation("Country").message,
          createLocationValidation("Country").test,
        ),
      vatNumber: Yup.string().when("isVATRegistered", {
        is: () => isVATRegistered,
        then: (schema) =>
          schema
            .trim()
            .required(VALIDATION_MESSAGES.REQUIRED("Your VAT Number"))
            .matches(
              VALIDATION_PATTERNS.VAT_NUMBER,
              "VAT Number must contain only letters and numbers",
            )
            .max(
              VALIDATION_LIMITS.VAT_NUMBER.MAX,
              VALIDATION_MESSAGES.MAX_LENGTH(
                "VAT Number",
                VALIDATION_LIMITS.VAT_NUMBER.MAX,
              ),
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: (values) => {
      console.log(color, "color");
      // console.log(values.phone.replace(/[()]/g, ""));
      console.log(
        "Company info",
        values.phone.replace(/[()]/g, "").replace(/\s+/g, ""),
      );
      dispatch(
        businessProfileSetup({
          name: values.businessName?.trim(),
          phone: values.phone.replace(/[()]/g, "").replace(/\s+/g, ""),
          logo: selectedLogoFile ?? null,
          city: values.city?.trim(),
          // state: values.state?.trim(),
          country: values.country?.trim(),
          postcode: values.zipCode?.trim(),
          address: values.streetAddress?.trim(),
          isUpdate: true,
          brand_color: color,
          vat_number: isVATRegistered ? values.vatNumber?.trim() : "",
          is_company_name_show: isShowCompanyName ? 1 : 0,
        }),
      );
    },
  });
  document.title = "Profile | EaziQuote";
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Profile" pageTitle="Settings" url="/settings" />

        <Card className="bg-white br-10">
          <CardHeader
            className="bg-white m-0"
            style={{ borderTopLeftRadius: 10, borderTopRightRadius: 10 }}
          >
            <Nav
              className="nav-tabs-custom rounded card-header-tabs border-bottom-0 "
              role="tablist"
            >
              <NavItem>
                <NavLink
                  to="#"
                  onClick={() => setActiveTab("1")}
                  className={classnames({ active: activeTab === "1" })}
                  style={{
                    fontSize: 16,
                    fontWeight: activeTab === "1" ? 500 : 400,
                    fontFamily: "Inter Tight",
                    color: activeTab === "1" ? "#082B60" : "#2D2D2D",
                  }}
                >
                  Personal Details
                </NavLink>
              </NavItem>

              <NavItem>
                <NavLink
                  to="#"
                  onClick={() => setActiveTab("2")}
                  className={classnames({ active: activeTab === "2" })}
                  style={{
                    fontSize: 16,
                    fontWeight: activeTab === "2" ? 500 : 400,
                    fontFamily: "Inter Tight",
                    color: activeTab === "2" ? "#082B60" : "#2D2D2D",
                  }}
                >
                  Business Info
                </NavLink>
              </NavItem>
            </Nav>
          </CardHeader>

          <CardBody className="p-4 mt-0 pt-0">
            <TabContent activeTab={activeTab}>
              <TabPane tabId="1">
                <PersonalDetails
                  validation={personalDetailValidation}
                  selectedFile={selectedFile}
                  setSelectedFile={setSelectedFile}
                  isLoading={isLoading}
                />
              </TabPane>

              <TabPane tabId="2">
                <BusinessInfo
                  selectedFile={selectedLogoFile}
                  setSelectedFile={setSelectedLogoFile}
                  validation={businessInfoValidation}
                  color={color}
                  setColor={setColor}
                  isOn={isVATRegistered}
                  setIsOn={setIsVATRegistered}
                  isLoading={isLoading}
                  isShowCompanyName={isShowCompanyName}
                  handleToggleShowCompanyName={() =>
                    setIsShowCompanyName((prev) => !prev)
                  }
                />
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default Profile;
